// 01 — what the speaker is answering, and the item the answer belongs to.
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { Textarea } from "@bindery/ui/components/ui/textarea";
import { cn } from "@bindery/ui/lib/utils";
import {
  type ItemRef,
  MAX_PROMPT_CHARS,
  MAX_PROMPT_WORDS,
} from "@bindery/speech/contract";
import {
  ITEM_KIND_LABELS,
  ITEM_KINDS,
  measurePrompt,
  type PromptFields,
} from "../lib/prompt";
import { Panel } from "./panel";

const isItemKind = (value: string): value is ItemRef["kind"] =>
  (ITEM_KINDS as readonly string[]).includes(value);

export function PromptPanel({
  fields,
  onChange,
  onNewItem,
}: {
  fields: PromptFields;
  onChange: (patch: Partial<PromptFields>) => void;
  onNewItem: () => void;
}) {
  const measure = measurePrompt(fields.promptText);
  return (
    <Panel
      index="01"
      title="Prompt"
      aside={
        <Button variant="ghost" size="xs" onClick={onNewItem}>
          New item
        </Button>
      }
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="prompt" className="lab-label">
            Question text
          </Label>
          <span
            className={cn(
              "lab-readout text-xs",
              measure.withinLimits
                ? "text-muted-foreground"
                : "text-destructive font-medium",
            )}
          >
            {measure.words}/{MAX_PROMPT_WORDS} words · {measure.chars}/
            {MAX_PROMPT_CHARS} chars
          </span>
        </div>
        <Textarea
          id="prompt"
          rows={4}
          value={fields.promptText}
          aria-invalid={!measure.withinLimits}
          onChange={(e) => onChange({ promptText: e.target.value })}
          className="text-sm"
        />
        <p className="text-muted-foreground text-xs">
          Sent to the provider as ASR context. Empty is allowed; over the limit,
          the page will not submit.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="item-kind" className="lab-label">
            Item kind
          </Label>
          <select
            id="item-kind"
            value={fields.itemKind}
            onChange={(e) => {
              if (isItemKind(e.target.value))
                onChange({ itemKind: e.target.value });
            }}
            className="border-input bg-card h-9 rounded-sm border px-2 font-mono text-xs"
          >
            {ITEM_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {ITEM_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="language" className="lab-label">
            Language
          </Label>
          <Input
            id="language"
            value={fields.language}
            onChange={(e) => onChange({ language: e.target.value })}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="item-id" className="lab-label">
            Item id
          </Label>
          <Input
            id="item-id"
            value={fields.itemId}
            onChange={(e) => onChange({ itemId: e.target.value })}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="attempt" className="lab-label">
            Attempt (next submit)
          </Label>
          <Input
            id="attempt"
            type="number"
            min={1}
            max={10}
            value={fields.attempt}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n)) onChange({ attempt: n });
            }}
            className="lab-readout text-xs"
          />
        </div>
      </div>
    </Panel>
  );
}
